import { z } from "zod/v4";
import { AgentType, InteractionCancelReason } from "../shared";
import {
  AgenticSettingsReviewSuggestionReason,
  CONTEXT_INDEX_FILENAME,
  AGENTIC_SETTINGS_DEFINITION_STATUSES,
} from "../agentic-settings-entities/types";
import { PersonalizationGraphSchema } from "../skills/personalization/schema";
import { IdrChangePlanResponseSchema } from "../skills/idr/llm-output";
import { suggestionBatchResponseSchema } from "../agents/agent-suggestion-batches/suggestion-batch-schema";
import { reportSuggestionsResponseSchema } from "../agents/report-suggestions/schema";
import {
  BestPracticeCategory,
  CUSTOM_CATEGORY_NAME,
  RuleName,
} from "@hightouch/core/content/best-practices/types";
import { copyExamplesReviewOutputContentInputSchema } from "@hightouch/core/content/lcm-copy-examples/schema";
import { REVIEW_TARGET_WIRE_OPERATIONS } from "../agentic-settings-entities/review-targets/schema";
import { journeyMessageEnvelopeSchema } from "@hightouch/lib/journeys/envelope";
import { CalendarDefinitionsResponseSchema } from "../skills/campaign-insights/definition-suggestions";

export type SchemaMessageDefinition<
  TLLMSchema extends z.ZodObject<{ type: z.ZodLiteral }> = any,
> = {
  type: string;
  schema: TLLMSchema;
};

export const BaseAssistantMessage = {
  type: "assistant",
  schema: z.object({
    type: z.literal("assistant"),
    content: z.string()
      .describe(`Used to display formatted text responses. Content MUST be in markdown. Use markdown tables, bold, italics, and lists appropriately for readability.

Formatting rules:
- Tables: One line per row. For multi-line cells, use <br>. Replace pipes in content with &#124;
- Lists: Use only markdown syntax (-, *, or numbers). No Unicode bullets (•, ‣, etc.)
- Nested lists: Use 4 spaces for indentation per nesting level. Example:

1. item
    - nested (4 spaces)
    - nested (4 spaces)
2. item
    - nested (4 spaces)
        - double nested (8 spaces)

Note: Additional rich components (charts, cards, etc.) may be available. See the system prompt for component documentation and usage instructions.`),
    duration: z.number().nullable(),
  }),
} satisfies SchemaMessageDefinition;

export enum InteractionStatus {
  Pending = "pending",
  Complete = "complete",
  Canceled = "canceled",
  Failed = "failed",
}

export const AudienceResponseSchema = z.object({
  type: z.literal("audience"),
  content: z.object({
    name: z.string(),
    description: z.string(),
    conditions: z.array(z.any()),
    // Subset value ids applied to the audience. Array = authoritative full
    // list ([] = remove all); null/undefined (including legacy stored
    // messages) = unspecified, preserve the audience's existing subsets.
    subsets: z.array(z.string()).nullish(),
    // Used to store the existing audience that this message is based on.
    existingId: z.number().nullable(),
    // Used to store the id of the audience that was created from this message.
    newId: z.number().nullable(),
  }),
});

// Exit criteria schema - uses audience-style condition format
const ExitCriteriaSchema = z.object({
  type: z.enum(["and", "or"]),
  conditions: z.array(z.any()),
});

export const LegacyJourneyContentSchema = z.object({
  name: z.string(),
  description: z.string(),
  parent_model_id: z.number(),
  status: z
    .enum(["disabled", "draining", "live", "staged"])
    .default("disabled"),
  schedule: z.object({
    type: z.literal("interval"),
    schedule: z.object({
      interval: z.object({
        unit: z.string(),
        quantity: z.number(),
      }),
    }),
  }),
  exit_criteria: z.union([z.null(), ExitCriteriaSchema]),
  nodes: z.array(z.any()),
  edges: z.array(z.any()),
  notes: z.array(z.any()),
  newId: z.string().nullish(),
});

export type LegacyJourneyContent = z.infer<typeof LegacyJourneyContentSchema>;

export const JourneyResponseSchema = z.object({
  type: z.literal("journey"),
  content: z.union([journeyMessageEnvelopeSchema, LegacyJourneyContentSchema]),
});

// A card for an existing journey the agent is discussing. It carries no graph,
// so it is its own message type rather than a third shape of `journey`.
export const JourneyReferenceResponseSchema = z.object({
  type: z.literal("journey_reference"),
  content: z.object({
    existingId: z.string().min(1),
    name: z.string(),
    description: z.string().optional(),
  }),
});

// A card for a saved Native Delivery email version. The message row holds its name and campaign, so the card carries only ids.
export const NdEmailMessageResponseSchema = z.object({
  type: z.literal("nd_email_message"),
  content: z.object({
    message_id: z
      .string()
      .uuid()
      .describe("Message ID returned by save_email."),
    version: z
      .number()
      .int()
      .positive()
      .describe("Version returned by the latest successful save_email."),
  }),
});

export const TraitResponseSchema = z.object({
  type: z.literal("trait"),
  content: z.object({
    name: z.string(),
    description: z.string().optional(),
    type: z.string(),
    config: z.any(),
    relationshipId: z.string().nullable(),
    parentModelId: z.string(),
    isTemplate: z.boolean().nullable(),
    // Used to store the existing trait that this message is based on.
    existingId: z.string().optional(),
    // Used to store the id of the trait that was created from this message.
    newId: z.string().optional(),
    // Similar to charts - allows the frontend to track if the trait has unsaved
    // changes to avoid updating the full message content on form state changes
    hasUnsavedChanges: z.boolean().nullable(),
  }),
});

export const AdBriefAssetSchema = z.object({
  assetId: z
    .string()
    .optional()
    .describe(
      "ID of an existing asset to edit. If present, creates a new version of that asset. If omitted, creates a new asset.",
    ),
  assetName: z
    .string()
    .describe(
      "A short, descriptive name for the asset, 3-5 words max (e.g., 'Hero Image', 'Product Showcase', 'CTA Banner').",
    ),
  assetType: z
    .string()
    .describe(
      "The type of asset (e.g., 'image', 'video', 'carousel', 'static', 'animated').",
    ),
  assetUrl: z.string().describe("A publicly accessible URL to the asset."),
});

export const AdBriefResponseSchema = z
  .object({
    type: z.literal("ad_brief"),
    content: z.object({
      title: z
        .string()
        .describe(
          "A short, specific identifier for the concept (3-5 words) that captures the angle being tested. Should be memorable and distinct. Examples: 'Autoship Convenience Play', 'Pet Parent Guilt Relief', 'January Sale Urgency Test'",
        ),
      description: z
        .string()
        .describe(
          "1-2 sentences explaining why we're suggesting this concept. Include the insight that spurred it, competitive inspiration, or strategic rationale.",
        ),
      imageUrl: z
        .string()
        .describe(
          "A publicly accessible URL to a preview image for the ad brief. This image is displayed in the card thumbnail.",
        ),
      markdown: z
        .string()
        .describe(
          "The ad brief content in markdown format. Include details about the ad concept, target audience, messaging, creative direction, and any other relevant information.",
        ),
      assets: z
        .array(AdBriefAssetSchema)
        .describe(
          "An array of assets associated with this ad brief - images, videos, or other creative materials.",
        ),
    }),
  })
  .describe(
    "Used to generate ad briefs for specific advertisements. The brief should contain strategic direction for a single ad including creative concept, messaging, and targeting details. Format the content as professional markdown.",
  );

export const AdBriefMessage = {
  type: "ad_brief",
  schema: AdBriefResponseSchema,
} satisfies SchemaMessageDefinition;

export const AdBriefAssetResponseSchema = z
  .object({
    type: z.literal("ad_brief_asset"),
    content: AdBriefAssetSchema.extend({
      svgOverlay: z
        .string()
        .optional()
        .describe(
          "Optional SVG overlay document. Contains the full SVG with a background <image> element and overlay elements (text, buttons, shapes) on top.",
        ),
    }),
  })
  .describe(
    "Used to generate or edit individual ad creative assets. Output this when creating or modifying a single asset (image, video, etc.).",
  );

export const AdBriefAssetMessage = {
  type: "ad_brief_asset",
  schema: AdBriefAssetResponseSchema,
} satisfies SchemaMessageDefinition;

function downloadableFileResponseSchema<T extends string>(args: {
  type: T;
  format: string;
  titleExamples: string;
  descriptionText: string;
  filenameExample: string;
}) {
  return z
    .object({
      type: z.literal(args.type),
      content: z.object({
        title: z
          .string()
          .describe(
            `A short, descriptive title for the ${args.format} (e.g., ${args.titleExamples}).`,
          ),
        description: z.string().optional().describe(args.descriptionText),
        filename: z
          .string()
          .describe(
            `The filename for the ${args.format} download (should end with .${args.type}, e.g., '${args.filenameExample}').`,
          ),
        // File reference to S3 storage. Stored when message is created.
        // Format: "outputs/{filename}" - relative to the invocation directory in S3.
        fileReference: z
          .string()
          .optional()
          .describe(
            `Reference to the ${args.format} file stored in S3. Resolved to a download URL at query time.`,
          ),
        // Resolved download URL. Populated at query time from fileReference.
        url: z
          .string()
          .optional()
          .describe(
            `Download URL for the ${args.format} file. Resolved from fileReference at query time.`,
          ),
      }),
    })
    .describe(
      `Use to provide a downloadable ${args.format} to the user. It will be displayed as a card with a download button.`,
    );
}

export const CsvResponseSchema = downloadableFileResponseSchema({
  type: "csv",
  format: "CSV",
  titleExamples: "'Campaign Performance Data', 'User Segment Analysis'",
  descriptionText:
    "An optional description explaining what data is contained in the CSV.",
  filenameExample: "campaign-performance.csv",
});

export const CsvMessage = {
  type: "csv",
  schema: CsvResponseSchema,
} satisfies SchemaMessageDefinition;

export const PptxResponseSchema = downloadableFileResponseSchema({
  type: "pptx",
  format: "PPTX",
  titleExamples: "'Q4 Campaign Review', 'Audience Insights'",
  descriptionText:
    "An optional description of what the deck covers and who it is for.",
  filenameExample: "q4-campaign-review.pptx",
});

export const PptxMessage = {
  type: "pptx",
  schema: PptxResponseSchema,
} satisfies SchemaMessageDefinition;

export const TurnCompleteMessage = {
  type: "turn_complete",
  schema: z
    .object({
      type: z.literal("turn_complete"),
      content: z.null(),
    })
    .describe(
      `Use to indicate that the turn is complete without providing any additional information.`,
    ),
} satisfies SchemaMessageDefinition;

export const ToolMessage = {
  type: "tool",
  schema: z.object({
    type: z.literal("tool"),
    duration: z.number().nullable(),
    content: z.array(
      z.object({
        // The call's id in llm_chat_messages.tool_calls, which amp_action_tool_calls also keys on.
        id: z.string().optional(),
        name: z.string(),
        arguments: z.any(),
        result: z.string().nullable(), // null if the tool call is still pending
        summary: z.string().nullable(),
        status: z.enum(["pending", "complete"]),
        agentType: z.string().nullable(), // The type of subagent if this is a subagent call
        children: z.array(z.any()).nullable(), // Nested subagent messages
        // Progressive preview image for an in-flight tool call (currently
        // build_static_ad), resolved from the tool call's message metadata.
        // Lets the UI paint a partial render into a generating placeholder.
        previewUrl: z.string().nullable().optional(),
        // Final executed warehouse SQL for query_warehouse, resolved from the
        // tool call's message metadata. Deliberately stripped from the tool
        // result so the agent never sees it.
        executedSql: z.string().nullable().optional(),
      }),
    ),
  }),
} satisfies SchemaMessageDefinition;

export const QuizAnswerSummarySchema = z.object({
  title: z.string().optional(),
  actionLabel: z.string().optional(),
  totalQuestions: z.number().int().optional(),
  questions: z.array(
    z.object({
      id: z.string().optional(),
      label: z.string(),
      skipped: z.boolean().optional(),
      answers: z.array(
        z.object({
          label: z.string(),
          detail: z.string().optional(),
        }),
      ),
      other: z.string().optional(),
    }),
  ),
});

export const ModeTransitionSummarySchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  actionLabel: z.string().optional(),
  targetAgentMode: z.enum(["auto", "plan"]),
});

const nonBlankSubmittedContextValue = z
  .string()
  .refine((value) => value.trim().length > 0);

export const SubmittedContext = z.object({
  kind: z.enum(["send", "insight"]),
  id: nonBlankSubmittedContextValue,
  label: nonBlankSubmittedContextValue,
});

export type SubmittedContext = z.infer<typeof SubmittedContext>;

export const UserMessage = {
  type: "user",
  schema: z.object({
    type: z.literal("user"),
    content: z.object({
      text: z.string(),
      files: z
        .array(
          z.object({
            fileName: z.string(),
            mimeType: z.string(),
            url: z.string().optional(),
          }),
        )
        .nullable(),
      // Note: for now we're only supporting one custom agent per message, so we're going to use a single object instead of an array.
      // `name` is the current custom agent name (or historical name if deleted)
      // `prompt` is the historical prompt at time of submission
      customAgent: z
        .object({
          id: z.string(),
          name: z.string(),
          prompt: z.string(),
          isDeleted: z.boolean(),
        })
        .nullable(),
      submittedContext: z.array(SubmittedContext).optional(),
      elementChanges: z
        .array(
          z.object({
            label: z.string(),
            annotation: z.string(),
          }),
        )
        .nullable()
        .optional(),
      // Email components the user attached to this message. Persisted so the
      // bubble can re-render the attachment in history; the HTML preview is
      // re-fetched by id at render time, so only the reference is stored here.
      components: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            componentType: z.string().nullable(),
          }),
        )
        .nullable()
        .optional(),
      // Context Hub skills the user picked for this message, by reference.
      skills: z
        .array(z.object({ id: z.string(), name: z.string() }))
        .optional(),
      focusedFilter: z
        .object({
          label: z.string(),
          conditionId: z.string().optional(),
          conditionType: z
            .enum([
              "property",
              "trait",
              "event",
              "number_of",
              "segment_set",
              "journey_set",
              "funnel",
              "reference_property",
            ])
            .optional(),
          source: z.enum(["insights", "manual"]).optional(),
        })
        .nullable()
        .optional(),
      promptFacade: z
        .object({
          kind: z.string(),
          label: z.string(),
        })
        .nullable()
        .optional(),
      quizAnswerSummary: QuizAnswerSummarySchema.optional(),
      modeTransitionSummary: ModeTransitionSummarySchema.optional(),
      // True while the message waits for the agent. Absent for a message that the model received.
      waiting: z.literal(true).optional(),
      actionPlanApproval: z.object({ planId: z.string() }).optional(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const ReasoningMessage = {
  type: "reasoning",
  schema: z.object({
    type: z.literal("reasoning"),
    content: z.object({
      summary: z.string().nullable(),
      text: z.string(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const WebSearchMessage = {
  type: "web_search",
  schema: z.object({
    type: z.literal("web_search"),
    content: z.object({
      action: z
        .object({
          type: z.string(),
        })
        .catchall(z.any()),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const CodeInterpreterMessage = {
  type: "code_interpreter",
  schema: z.object({
    type: z.literal("code_interpreter"),
    content: z.object({
      code: z.string(),
      outputs: z.array(
        z.discriminatedUnion("type", [
          z.object({
            type: z.literal("image"),
            url: z.string(),
          }),
          z.object({
            type: z.literal("logs"),
            logs: z.string(),
          }),
        ]),
      ),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const ErrorMessage = {
  type: "error",
  schema: z.object({
    type: z.literal("error"),
    content: z.object({
      title: z.string(),
      message: z.string().nullable(),
      internalMessage: z.string().nullable(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const CanceledMessage = {
  type: "canceled",
  schema: z.object({
    type: z.literal("canceled"),
    content: z.object({
      reason: z
        .enum([
          InteractionCancelReason.UserCanceled,
          InteractionCancelReason.Superseded,
        ])
        .nullable(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const SuggestionsMessage = {
  type: "suggestions",
  schema: z.object({
    type: z.literal("suggestions"),
    content: z.array(
      z.object({
        type: z.enum([
          AgentType.Audience,
          AgentType.Journey,
          AgentType.Marketing,
          AgentType.Campaigns,
          AgentType.Routing,
          AgentType.Trait,
          "general",
        ]),
        prompt: z.string(),
      }),
    ),
  }),
} satisfies SchemaMessageDefinition;

export const EmailMessage = {
  type: "email",
  schema: z.object({
    type: z.literal("email"),
    content: z.object({
      subjectLine: z.string(),
      preheader: z.string(),
      html: z.string(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const ContentEmailMessage = {
  type: "content_email",
  schema: z.object({
    type: z.literal("content_email"),
    content: z.object({
      name: z.string(),
      // Optional: components are headless fragments with no subject/preheader.
      subject: z.string().optional(),
      preheader: z.string().optional(),
      version_id: z.string().optional(),
      email_id: z.string().optional(),
    }),
  }),
} satisfies SchemaMessageDefinition;

export const AmpFileResponseSchema = z.object({
  type: z.literal("amp_file"),
  content: z.object({
    fileId: z
      .string()
      .uuid()
      .describe(
        "The Library file id a tool returned, or one a specialist reported.",
      ),
  }),
});

export const AmpFileMessage = {
  type: "amp_file",
  schema: AmpFileResponseSchema,
} satisfies SchemaMessageDefinition;

const bestPracticeCategorySchema = z.union([
  z.nativeEnum(BestPracticeCategory),
  z.literal(CUSTOM_CATEGORY_NAME),
]);
const ruleNameSchema = z.union([z.nativeEnum(RuleName), z.string()]);

export const EmailBestPracticeAnalysisResponseSchema = z.object({
  type: z.literal("email_best_practice_analysis"),
  content: z.object({
    description: z
      .string()
      .describe(
        "A single sentence concisely describing the layout of the email (its overall structure and key sections, e.g. 'A single-column layout with a logo header, hero image, headline and body copy, a CTA button, and a footer with social links').",
      ),
    adhered: z
      .array(
        z.object({
          category: bestPracticeCategorySchema.describe(
            "The best practice category",
          ),
          name: ruleNameSchema.describe("The rule identifier"),
        }),
      )
      .describe("Best practices that were already being followed correctly"),
    fixed: z
      .array(
        z.object({
          category: bestPracticeCategorySchema.describe(
            "The best practice category",
          ),
          name: ruleNameSchema.describe("The rule identifier"),
          fix: z.string().describe("What was changed to fix the violation"),
        }),
      )
      .describe("Best practices violations that were fixed"),
    revisedHtml: z
      .object({
        filePath: z.string(),
      })
      .describe("File reference to the revised HTML output"),
  }),
});

export const EmailBestPracticeAnalysisMessage = {
  type: "email_best_practice_analysis",
  schema: EmailBestPracticeAnalysisResponseSchema,
} satisfies SchemaMessageDefinition;

export const EmailComponentCanonicalizationResponseSchema = z.object({
  type: z.literal("email_component_canonicalization"),
  content: z.object({
    name: z
      .string()
      .describe(
        "A concise, human-readable name for the component, 2-5 words capturing what it is and its role (e.g. 'Primary footer', 'Promo hero banner', 'Legal disclosure'). Avoid punctuation and the word 'component'.",
      ),
    componentType: z
      .string()
      .describe(
        "A short, title-case category for the component (e.g. 'Header', 'Footer', 'Hero', 'Disclosure', 'Banner', 'Navigation'). No trailing punctuation.",
      ),
    description: z
      .string()
      .describe(
        "A single sentence concisely describing the layout of the component (its structure and key elements, e.g. 'A two-column row with a product image on the left and a headline, body copy, and CTA button on the right').",
      ),
    changes: z
      .array(z.string())
      .describe(
        "Short descriptions of the canonicalization changes applied (e.g. responsive class remaps, removed <style> block, single-root wrapping).",
      ),
    canonicalHtml: z
      .object({
        filePath: z.string(),
      })
      .describe("File reference to the canonicalized component HTML output"),
  }),
});

export const EmailComponentCanonicalizationMessage = {
  type: "email_component_canonicalization",
  schema: EmailComponentCanonicalizationResponseSchema,
} satisfies SchemaMessageDefinition;

// Only old sessions carry this; no agent emits it. Kept so their messages still parse.
export const EmailTemplateAnalysisResponseSchema = z.object({
  type: z.literal("email_template_analysis"),
  content: z.object({
    content_email_version_id: z
      .string()
      .uuid()
      .describe("The content_email_versions.id this analysis is for."),
    violations: z
      .array(
        z.object({
          violation: z
            .string()
            .min(1)
            .describe(
              "One concrete brand-context violation. Name the rule, cite the context entry by name, and quote the offending text from the template.",
            ),
        }),
      )
      .min(1)
      .describe(
        "List of concrete brand-context violations found in the template. Emit one entry per violation. If there are no violations, do not emit this message type at all.",
      ),
  }),
});

type ContextReviewWireOperation =
  (typeof REVIEW_TARGET_WIRE_OPERATIONS)[number];

export type ContextReviewWireOperations = readonly [
  ContextReviewWireOperation,
  ...ContextReviewWireOperation[],
];

const ContextReviewReasonSchema = z
  .enum([
    AgenticSettingsReviewSuggestionReason.Contradiction,
    AgenticSettingsReviewSuggestionReason.Redundancy,
    AgenticSettingsReviewSuggestionReason.Clarity,
    AgenticSettingsReviewSuggestionReason.Gap,
    AgenticSettingsReviewSuggestionReason.Stale,
  ])
  .describe("Category of issue this item addresses.");

export const contextReviewSuggestionSchema = (
  operations: ContextReviewWireOperations,
) =>
  z.object({
    entity_id: z
      .string()
      .nullable()
      .describe(
        "The entity being reviewed. Null when op_type is add_entity or update_brand_profile.",
      ),
    reviewed_entity_version_id: z
      .string()
      .nullable()
      .describe(
        `The version of entity_id the agent reviewed. Null when op_type is add_entity or update_brand_profile. For entity edits, must be the version currently shown in ${CONTEXT_INDEX_FILENAME} at review time.`,
      ),
    // Discriminated on op_type only: AJV (used by the agent runtime to validate response.json
    // against the draft-7 JSON Schema) rejects discriminator-union variants that share a
    // discriminator value.
    operation: z.discriminatedUnion("op_type", operations),
    card_summary: z
      .string()
      .optional()
      .describe(
        "Brief card caption describing what changes, at most 7 words; shorter is better. Avoid repeating the document title or operation label. Detail belongs in summary.",
      ),
    summary: z
      .string()
      .describe("Short rationale for this suggestion shown to the user."),
    reason: ContextReviewReasonSchema,
  });

// The output context-library agents emitted before save_context_suggestions.
// Saved messages keep this type with content `{ summary, agentContextReviewId }`, so the app still reads it.
export const ContextReviewResponseSchema = z.object({
  type: z.literal("context_review"),
  content: z.object({
    summary: z
      .string()
      .describe(
        "One-paragraph summary of what the review found across the workspace's context.",
      ),
    // Post-persist, the array is replaced with `agentContextReviewId`. The wire/agent
    // shape always carries it.
    suggestions: z
      .array(contextReviewSuggestionSchema(REVIEW_TARGET_WIRE_OPERATIONS))
      .describe(
        "Zero or more suggested operations. An empty array is valid when the review found nothing to change.",
      ),
  }),
});

export const ContextReviewMessage = {
  type: "context_review",
  schema: ContextReviewResponseSchema,
} satisfies SchemaMessageDefinition;

// References the review that save_context_suggestions saved this interaction.
export const ContextSuggestionsResponseSchema = z.object({
  type: z.literal("context_suggestions"),
  content: z.object({
    reviewId: z
      .string()
      .uuid()
      .describe("The `review_id` returned by save_context_suggestions."),
  }),
});

export const ContextSuggestionsMessage = {
  type: "context_suggestions",
  schema: ContextSuggestionsResponseSchema,
} satisfies SchemaMessageDefinition;

export const CopyExamplesReviewResponseSchema = z.object({
  type: z.literal("copy_examples_review"),
  // Plain object (not the `.transform()` schema) so this can be converted to JSON Schema for
  // the model. Normalization is applied downstream via parseCopyExamplesReviewOutputContent.
  content: copyExamplesReviewOutputContentInputSchema,
});

export const CopyExamplesReviewMessage = {
  type: "copy_examples_review",
  schema: CopyExamplesReviewResponseSchema,
} satisfies SchemaMessageDefinition;

// Persisted assistant messages replace the agent's potentially large copy-examples output
// with a pointer to the live review. Keep this separate from CopyExamplesReviewResponseSchema:
// only the full agent-output shape belongs in response.json validation.
export const PersistedCopyExamplesReviewMessageSchema = z.object({
  type: z.literal("copy_examples_review"),
  content: z.object({
    summary: z.string(),
    reviewId: z.uuid(),
  }),
});

export const SuggestionBatchResponseSchema = z.object({
  type: z.literal("suggestion_batch"),
  content: suggestionBatchResponseSchema,
});

export const SuggestionBatchMessageDef = {
  type: "suggestion_batch",
  schema: SuggestionBatchResponseSchema,
} satisfies SchemaMessageDefinition;

export const ReportSuggestionsMessageSchema = z.object({
  type: z.literal("report_suggestions"),
  content: reportSuggestionsResponseSchema,
});

export const ReportSuggestionsMessageDef = {
  type: "report_suggestions",
  schema: ReportSuggestionsMessageSchema,
} satisfies SchemaMessageDefinition;

// Points the backend at the run's payload file; the publication processor
// reads only this exact path from the current invocation's outputs.
export const CampaignInsightsResultMessageSchema = z
  .object({
    type: z.literal("campaign_insights_result"),
    content: z
      .object({
        schemaVersion: z.literal(1),
        payloadFile: z.literal("outputs/insights.json"),
      })
      .strict(),
  })
  .strict();

export const CampaignInsightsResultMessageDef = {
  type: "campaign_insights_result",
  schema: CampaignInsightsResultMessageSchema,
} satisfies SchemaMessageDefinition;

// No `definitionId` field: an id the agent invents is dropped here rather than
// reaching the save path. The persisted schema below adds it. The agent names an
// assumption to revise in `revisesDefinitionId` instead, because the UI reads a
// message's `definitionId` as "already saved as this memory".
const assumptionSchema = z.object({
  key: z
    .string()
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Must be lowercase kebab-case, such as `repeat-customer`.",
    )
    .nullish()
    .describe(
      "A short kebab-case key you choose for this assumption, unique within this response, such as `repeat-customer`.",
    ),
  calendarRole: z
    .enum(["metric", "segment"])
    .nullish()
    .describe(
      "Only for a new definition explicitly created through the Calendar quiz: the role the user chose. This lets Calendar select the saved definition; it does not assign a role to the shared definition.",
    ),
  assumption: z.string().describe("Short title for the assumption"),
  content: z
    .string()
    .describe(
      "The definition itself: what the concept is and how it is computed, naming any saved entry it builds on by title. No figures or findings you computed, no notes about this session, no evidence or reasoning (put those in `reasoning`), and no guidance on when to apply it (put that in `usageGuidance`).",
    ),
  usageGuidance: z
    .string()
    .nullish()
    .describe(
      "Optional, short and general guidance on when to use this assumption. Omit it unless you have evidence the assumption only applies in specific situations; assume an assumption is generally useful.",
    ),
  reasoning: z
    .string()
    .nullish()
    .describe(
      "Optional explanation of how you arrived at this assumption: the evidence you found and why you chose it over alternatives.",
    ),
  sql: z
    .string()
    .nullable()
    .describe(
      "The smallest SQL query that encodes this assumption's own rule, not the full query you ran. Use null if no SQL is applicable or if it would only restate another entry's rule.",
    ),
  statedByUser: z
    .boolean()
    .nullish()
    .describe(
      "True when the user themselves supplied what this assumption records, in this conversation or an earlier one, and especially when they asked you to remember it. False, null, or omitted when you inferred it from the schema, the data, or what a task seemed to need. Judge the substance, not who typed it: a user answering 'revenue is net_amount, completed orders only' is stated by the user; your own reading of the schema is not, even when they approved the answer it produced.",
    ),
  // A string rather than a uuid so one bad id cannot fail the whole output; the
  // processor validates it and saves nothing when it does not resolve.
  revisesDefinitionId: z
    .string()
    .nullish()
    .describe(
      "The ID of an existing unapproved assumption this one replaces, copied exactly from the entry's `ID:` line. Set it when you are correcting or refining that assumption, so it is saved as a new version of it. Null or omitted for a new concept. Don't change an approved definition: never set this to its ID.",
    ),
});

const referencedMemorySchema = z.object({
  definitionId: z
    .uuid({
      error:
        "Must be a definition's ID. A CR- change request is not a definition and is never cited.",
    })
    .nullish()
    .describe(
      "The ID line shown with this definition in the saved definitions files. Copy it exactly; never invent one.",
    ),
  title: z.string().describe("The entry's heading text, exactly as written."),
  content: z
    .string()
    .describe(
      "The entry's current wording below its heading, exactly as written, without any ```sql block or the ID, Scope and Proposed lines. Stop before any `Usage Guidance:` line or `#### Previous version` heading.",
    ),
});

const assumptionsContentSchema = z.object({
  assumptions: z
    .array(assumptionSchema)
    .describe("List of assumptions or business context to save as definitions"),
  referencedMemories: z
    .array(referencedMemorySchema)
    .default([])
    .describe(
      "List of existing definitions from the provided context that were directly referenced or used in generating this response. Only include definitions that you actually used to inform your analysis or assumptions. Use an empty array if no definitions were referenced.",
    ),
});

// A change to an approved definition, as messages from before these moved to context_review
// stored them. Still parsed so those messages render.
const definitionChangeRequestSchema = assumptionSchema
  .pick({
    assumption: true,
    content: true,
    sql: true,
    usageGuidance: true,
    reasoning: true,
  })
  .extend({
    // A string rather than a uuid so one bad id fails validation with a message the agent can act on.
    definitionId: z
      .string()
      .describe(
        "The ID of the approved definition this changes, copied exactly from the entry's `ID:` line.",
      ),
    changeSummary: z
      .string()
      .describe(
        "One short clause naming the change, such as `count refunds as negative revenue`.",
      ),
    reason: ContextReviewReasonSchema,
  });

const ASSUMPTIONS_DESCRIPTION =
  "Use to capture key business context, definitions, or assumptions that should be saved as definitions for future conversations. Include referencedMemories to show which existing definitions informed your response.";

export const AssumptionsAgentResponseSchema = z
  .object({
    type: z.literal("assumptions"),
    content: assumptionsContentSchema,
  })
  .describe(ASSUMPTIONS_DESCRIPTION);

// Persisted on the message. `definitionId` names the memory an assumption was saved as, so a
// later remount still resolves it after the memory's text is edited elsewhere. `versionId` and
// `status` record the definition version each entry cited; turns before version tracking lack them.
export const AssumptionsResponseSchemaWithDefinitionIds = z.object({
  type: z.literal("assumptions"),
  content: assumptionsContentSchema.extend({
    assumptions: z.array(
      assumptionSchema.extend({
        definitionId: z.string().nullish(),
        versionId: z.string().nullish(),
      }),
    ),
    // The review and suggestion each request was saved as.
    changeRequests: z
      .array(
        definitionChangeRequestSchema.extend({
          reviewId: z.string().nullish(),
          suggestionId: z.string().nullish(),
        }),
      )
      .optional(),
    referencedMemories: z
      .array(
        referencedMemorySchema.extend({
          versionId: z.string().nullish(),
          status: z.enum(AGENTIC_SETTINGS_DEFINITION_STATUSES).nullish(),
        }),
      )
      .default([]),
  }),
});

export const AssumptionsMessage = {
  type: "assumptions",
  schema: AssumptionsAgentResponseSchema,
} satisfies SchemaMessageDefinition;

export const QuizResponseSchema = z
  .object({
    type: z.literal("quiz"),
    content: z.object({
      title: z.string().describe("Short title for the quiz"),
      description: z
        .string()
        .optional()
        .describe("Optional context explaining why the questions matter"),
      layout: z
        .enum(["inline", "tabs"])
        .optional()
        .describe(
          "Use inline for one section. Use tabs when there are multiple distinct topic groups.",
        ),
      submitLabel: z
        .string()
        .optional()
        .describe(
          "Optional label for the submit button. KEEP IT SHORT — aim for 4 words or fewer; long labels wrap awkwardly in the input chip row. Supports the {{count}} placeholder, which is substituted with the total number of options the user has currently selected across the quiz (e.g. 'Build {{count}} ads'). Do NOT mention 'Exit Plan mode' or any mode-switch verbiage — when submitModeTransition is set, the UI surfaces it as a hover tooltip automatically.",
        ),
      submitLabelEmpty: z
        .string()
        .optional()
        .describe(
          "Optional label shown on the submit button when the user has not yet selected anything. Same length guidance as submitLabel (4 words or fewer). Use when the count-based submitLabel would read poorly at zero (e.g. submitLabel='Build {{count}} ads' + submitLabelEmpty='Pick at least one'). Also supports {{count}} (which renders as 0 here).",
        ),
      submitModeTransition: z
        .object({
          targetAgentMode: z
            .enum(["auto", "plan"])
            .describe("Agent mode to switch to before submitting answers"),
          promptPrefix: z
            .string()
            .optional()
            .describe(
              "Optional instructions to prepend to the synthesized quiz answer prompt",
            ),
        })
        .optional()
        .describe(
          "If present, submitting the quiz switches agent mode before sending the synthesized answer prompt.",
        ),
      secondaryActions: z
        .array(
          z.object({
            id: z.string().describe("Stable identifier for this action"),
            label: z
              .string()
              .describe(
                "Button label shown to the user. KEEP IT SHORT — aim for 4 words or fewer (e.g. 'Generate more', 'Add all'). Supports the {{count}} placeholder, which is substituted with the total number of options the user has currently selected across the quiz. Do NOT mention 'Exit Plan mode' or any mode-switch verbiage — when modeTransition is set, the UI surfaces it as a hover tooltip automatically.",
              ),
            labelEmpty: z
              .string()
              .optional()
              .describe(
                "Optional label shown when the user has not yet selected anything. Same length guidance as `label` (4 words or fewer). Use when the count-based label would read poorly at zero (e.g. label='Add {{count}} to brief' + labelEmpty='Add to brief').",
              ),
            prompt: z
              .string()
              .describe(
                "Prompt text sent when this action is clicked. If the user has made selections on the quiz, the synthesized quiz answer summary is automatically appended (and sent as metadata) so the agent sees the context.",
              ),
            modeTransition: z
              .object({
                targetAgentMode: z
                  .enum(["auto", "plan"])
                  .describe(
                    "Agent mode to switch to before sending the prompt",
                  ),
              })
              .optional()
              .describe(
                "If present, clicking the action switches agent mode before sending the prompt.",
              ),
            variant: z
              .enum(["secondary", "tertiary"])
              .optional()
              .describe("Optional visual treatment. Defaults to 'secondary'."),
          }),
        )
        .optional()
        .describe(
          "Optional alternate CTAs rendered alongside the primary submit. Use for shortcut paths like 'Generate more', 'Add all', or for branching follow-ups that don't require answering the quiz.",
        ),
      sections: z
        .array(
          z.object({
            id: z.string().describe("Stable identifier for this quiz section"),
            title: z.string().describe("Short section title"),
            description: z
              .string()
              .optional()
              .describe("Optional context for this section"),
            questions: z
              .array(
                z
                  .object({
                    id: z
                      .string()
                      .describe("Stable identifier for this question"),
                    label: z
                      .string()
                      .describe("Question label shown to the user"),
                    description: z
                      .string()
                      .optional()
                      .describe("Optional context for the question"),
                    selectionMode: z
                      .enum(["single", "multiple"])
                      .describe(
                        "Use single for mutually exclusive choices and multiple for combinable choices.",
                      ),
                    required: z
                      .boolean()
                      .optional()
                      .describe("Whether the user must answer this question"),
                    options: z
                      .array(
                        z.object({
                          id: z
                            .string()
                            .describe("Stable identifier for this option"),
                          label: z.string().describe("Option label"),
                          description: z
                            .string()
                            .optional()
                            .describe(
                              "Optional supporting text for the option",
                            ),
                          images: z
                            .array(
                              z.object({
                                src: z
                                  .string()
                                  .describe(
                                    "Image source. Prefer the relative output filename returned by the tool that produced this image (e.g. 'static-ad-mockups/<hash>.png') — the renderer resolves it to a workspace-scoped URL. Absolute URLs and data URLs are also accepted but generally aren't needed for tool-generated content.",
                                  ),
                                alt: z
                                  .string()
                                  .optional()
                                  .describe("Alt text for the option image"),
                              }),
                            )
                            .optional()
                            .describe(
                              "Optional images for visual choices, such as style directions or mockups.",
                            ),
                          promptValue: z
                            .string()
                            .describe(
                              "Text to include in the follow-up prompt if this option is selected",
                            ),
                          allowDetail: z
                            .boolean()
                            .optional()
                            .describe(
                              "Whether the user can add extra detail for this selected option",
                            ),
                          detailPlaceholder: z
                            .string()
                            .optional()
                            .describe(
                              "Placeholder for the option detail input",
                            ),
                          defaultSelected: z
                            .boolean()
                            .optional()
                            .describe(
                              "Pre-select this option. Use for the agent's best guess so the user can submit without changing it. For a single question, only the first defaultSelected option counts.",
                            ),
                        }),
                      )
                      .default([])
                      .describe(
                        "Choices the user can select. Omit for an open question answered only in free text, and set allowOther.",
                      ),
                    allowOther: z
                      .boolean()
                      .optional()
                      .describe(
                        "Whether the user can provide a custom answer. Required for a question without options.",
                      ),
                    otherPlaceholder: z
                      .string()
                      .optional()
                      .describe("Placeholder for the custom answer input"),
                  })
                  .refine(
                    (question) =>
                      question.options.length > 0 ||
                      question.allowOther === true,
                    {
                      message:
                        "A question needs at least one option, or allowOther: true for a free-text answer",
                      path: ["options"],
                    },
                  ),
              )
              .describe("Questions in this section"),
          }),
        )
        .describe("Quiz sections"),
    }),
  })
  .describe(
    "Use to ask the user a small set of structured questions when their input is needed. Follow the active skill instructions for when to ask questions and any agent-specific mode restrictions.",
  );

export const QuizMessage = {
  type: "quiz",
  schema: QuizResponseSchema,
} satisfies SchemaMessageDefinition;

export const GettySelectionResponseSchema = z
  .object({
    type: z.literal("getty_selection"),
    content: z.object({
      title: z
        .string()
        .describe(
          "Short heading naming what the user asked for, e.g. 'Pick a rocket launch photo'. Don't assign the photo a layout role the user didn't ask for.",
        ),
      options: z
        .array(
          z.object({
            gettyId: z
              .string()
              .min(1)
              .describe("A gettyId returned by search_getty_images"),
            reason: z
              .string()
              .optional()
              .describe("One short line on why this image fits the ad"),
          }),
        )
        .min(1)
        .max(12),
    }),
  })
  .describe(
    "Present Getty Images candidates from search_getty_images for the user to pick. The app shows thumbnails, licenses the user's picks against their Getty account, and sends you their titles in the next message. End your turn after emitting this.",
  );

export const GettySelectionMessage = {
  type: "getty_selection",
  schema: GettySelectionResponseSchema,
} satisfies SchemaMessageDefinition;

export const ModeTransitionResponseSchema = z
  .object({
    type: z.literal("mode_transition"),
    content: z.object({
      title: z.string().describe("Short title for the mode transition card"),
      description: z
        .string()
        .optional()
        .describe("Brief explanation of why the mode change is needed"),
      targetAgentMode: z
        .enum(["auto", "plan"])
        .describe("Agent mode to switch to before submitting the prompt"),
      submitLabel: z
        .string()
        .optional()
        .describe(
          "Optional label for the transition button. KEEP IT SHORT — aim for 4 words or fewer. Do NOT mention 'Exit Plan mode' / 'Enter Plan mode' in the label; the UI surfaces the mode switch as a hover tooltip automatically. Describe the action that follows (e.g. 'Build editable ads', 'Save and continue'), not the mode change itself.",
        ),
      prompt: z
        .string()
        .describe(
          "The user prompt to submit immediately after switching modes",
        ),
    }),
  })
  .describe(
    "Use to ask the user to switch agent modes before continuing a task that is unavailable in the current mode.",
  );

export const ModeTransitionMessage = {
  type: "mode_transition",
  schema: ModeTransitionResponseSchema,
} satisfies SchemaMessageDefinition;

const SaveMemoryResponseSchema = z
  .object({
    type: z.literal("save_memory"),
    content: z.object({
      memories: z.array(
        z.object({
          title: z.string().describe("Short, descriptive title for the memory"),
          content: z.string().describe("Detailed description or context"),
          sql: z
            .string()
            .nullable()
            .describe(
              "Optional SQL query that defines or validates this memory. Use null if no SQL is applicable.",
            ),
          scope: z
            .enum(["workspace", "customer_studio", "data_source"])
            .describe(
              "Scope of the memory: 'workspace' for general business context, 'customer_studio' for schema-specific info, 'data_source' for SQL/table-specific info",
            ),
          // Written back by the UI once the user saves the memory, so a later
          // remount knows which memory it became even after the memory's text
          // is edited elsewhere.
          definitionId: z
            .string()
            .nullish()
            .describe(
              "Set by the UI after the user saves this memory. Never set this yourself.",
            ),
        }),
      ),
      promptMessage: z
        .string()
        .describe(
          "A brief message to show the user explaining what memories you're suggesting to save",
        ),
    }),
  })
  .describe(
    "Use when the user asks to save business context, definitions, or information as memories. Determine the appropriate scope based on whether the memory is general business context (workspace), relates to Customer Studio schema (customer_studio), or involves SQL/data source specifics (data_source).",
  );

export const SaveMemoryMessage = {
  type: "save_memory",
  schema: SaveMemoryResponseSchema,
} satisfies SchemaMessageDefinition;

export const PersonalizationResponseSchema = z.object({
  type: z.literal("personalization"),
  content: PersonalizationGraphSchema,
});

export const CampaignArtifactResponseSchema = z.object({
  type: z.literal("campaign_artifact"),
  content: z.object({
    filePath: z.string().describe("Path to the artifact file"),
    filename: z.string().describe("The name of the artifact file"),
    title: z.string().describe("Title of the artifact"),
    description: z.string().describe("Description of the artifact"),
    artifactType: z.enum([
      "user_upload",
      "agent_generated",
      "campaign_brief",
      "content_copy",
      "concept_brief",
    ]),
  }),
});

export const CampaignArtifactMessage = {
  type: "campaign_artifact",
  schema: CampaignArtifactResponseSchema,
} satisfies SchemaMessageDefinition;

/**
 * High-level campaign concept output - used for initial ideation before fanning out
 * to generate detailed concept briefs. These are general ideas grounded in data
 * rather than fully fleshed out audience + messaging strategies.
 */
export const CampaignConceptResponseSchema = z.object({
  type: z.literal("campaign_concept"),
  content: z.object({
    concepts: z.array(
      z.object({
        id: z
          .string()
          .describe(
            "Unique identifier for this concept (used for tracking through the pipeline)",
          ),
        name: z.string().describe("Short name for the concept (3-5 words)"),
        rationale: z
          .string()
          .describe(
            "Explanation of why this concept is relevant, grounded in data analysis findings",
          ),
        dataInsights: z
          .array(z.string())
          .describe(
            "Key data points or trends that support this concept (e.g., metrics, behavioral patterns)",
          ),
        targetBehavior: z
          .string()
          .describe(
            "The customer behavior or pattern this concept addresses (e.g., 'lapsed purchasers', 'high-engagement low-conversion')",
          ),
        strategicDirection: z
          .string()
          .describe(
            "High-level strategic direction for messaging (2-3 sentences)",
          ),
        sessionId: z
          .string()
          .describe(
            "Session ID of the dispatched concept brief session (from dispatch_concept_briefs tool response)",
          ),
      }),
    ),
  }),
});

export const CampaignConceptMessage = {
  type: "campaign_concept",
  schema: CampaignConceptResponseSchema,
} satisfies SchemaMessageDefinition;

/**
 * Concept brief output - references a saved concept brief artifact.
 * The actual brief content is stored in the campaign_artifacts table.
 * This output type is emitted after calling finalize_concept_brief tool
 * to allow the UI to display the artifact.
 */
export const ConceptBriefResponseSchema = z.object({
  type: z.literal("concept_brief"),
  content: z.object({
    artifactId: z
      .string()
      .describe("The ID of the saved concept brief artifact"),
    conceptId: z
      .string()
      .optional()
      .describe("The ID of the created concept record (if available)"),
  }),
});

export const ConceptBriefMessage = {
  type: "concept_brief",
  schema: ConceptBriefResponseSchema,
} satisfies SchemaMessageDefinition;

export const DeveloperMessage = {
  type: "developer",
  schema: z.object({
    type: z.literal("developer"),
    content: z.string(),
  }),
} satisfies SchemaMessageDefinition;

export const ArtifactReviewResponseSchema = z
  .object({
    type: z.literal("artifact_review"),
    content: z.object({
      artifact_id: z
        .string()
        .uuid()
        .describe("ID of the artifact that was reviewed"),
      artifact_version_id: z
        .string()
        .uuid()
        .nullable()
        .describe("Version of the artifact that was reviewed"),
      comments: z
        .array(
          z.object({
            custom_agent_id: z
              .string()
              .uuid()
              .describe("custom_agent_id of the reviewer"),
            content: z.string().describe("The review comment"),
            type: z
              .enum(["critical", "warning", "suggestion"])
              .describe("Severity of the comment"),
          }),
        )
        .describe("Array of review comments from all reviewers"),
      resolved_comment_ids: z
        .array(z.string().uuid())
        .optional()
        .describe(
          "IDs of previous comments that have been addressed in this version",
        ),
    }),
  })
  .describe(
    "Indicates that an artifact review has completed. Displays feedback from configured reviewers.",
  );

export const ArtifactReviewMessage = {
  type: "artifact_review",
  schema: ArtifactReviewResponseSchema,
} satisfies SchemaMessageDefinition;

export type ArtifactReviewMessage = z.infer<
  typeof ArtifactReviewResponseSchema
>;

export const ReportPromptResponseSchema = z
  .object({
    type: z.literal("report_prompt"),
    content: z.object({
      reportPrompt: z
        .string()
        .describe(
          "The complete, updated report prompt/instructions. This replaces the existing prompt entirely.",
        ),
      reportName: z
        .string()
        .optional()
        .describe(
          "A concise, human-readable report name in sentence case. Only set when creating a new report.",
        ),
    }),
  })
  .describe(
    "Used to output an updated report prompt. The reportPrompt field contains the full replacement prompt text.",
  );

export const ArtifactImageResponseSchema = z.object({
  type: z.literal("artifact_image"),
  content: z.object({
    name: z.string().describe("Display name for the image"),
    filename: z.string().describe("Relative path or URL to the image"),
    alt: z.string().optional().describe("Alt text for the image"),
    contentImageId: z
      .string()
      .nullable()
      .describe("Set after saving to content library"),
    nodeId: z
      .string()
      .nullable()
      .optional()
      .describe("Content node ID, extracted from filename when it is a UUID"),
  }),
});

export const ArtifactImageMessage = {
  type: "artifact_image",
  schema: ArtifactImageResponseSchema,
} satisfies SchemaMessageDefinition;

export const getSuggestionMessageSchema = (
  agentTypes: (AgentType | "general")[] = [
    AgentType.Audience,
    AgentType.Journey,
    AgentType.Marketing,
    AgentType.Campaigns,
    AgentType.Routing,
    "general",
  ],
  opts?: { min?: number; max?: number },
): SchemaMessageDefinition => {
  return {
    type: "suggestions",
    schema: z.object({
      type: z.literal("suggestions"),
      content: z
        .array(
          z.object({
            type: z.enum(agentTypes),
            prompt: z.string(),
          }),
        )
        .min(opts?.min ?? 0)
        .max(opts?.max ?? 3),
    }),
  } satisfies SchemaMessageDefinition;
};

export type ToolMessage = z.infer<typeof ToolMessage.schema>;
export type UserMessage = z.infer<typeof UserMessage.schema>;
export type QuizAnswerSummary = z.infer<typeof QuizAnswerSummarySchema>;
export type ModeTransitionSummary = z.infer<typeof ModeTransitionSummarySchema>;
export type AssistantMessage = z.infer<typeof BaseAssistantMessage.schema>;
export type TurnCompleteMessage = z.infer<typeof TurnCompleteMessage.schema>;
export type AudienceMessage = z.infer<typeof AudienceResponseSchema>;
export type TraitMessage = z.infer<typeof TraitResponseSchema>;
export type JourneyMessage = z.infer<typeof JourneyResponseSchema>;
export type JourneyReferenceMessage = z.infer<
  typeof JourneyReferenceResponseSchema
>;
export type AdBriefAsset = z.infer<typeof AdBriefAssetSchema>;
export type AdBriefMessage = z.infer<typeof AdBriefResponseSchema>;
export type AdBriefAssetMessage = z.infer<typeof AdBriefAssetResponseSchema>;
export type CsvMessage = z.infer<typeof CsvResponseSchema>;
export type PptxMessage = z.infer<typeof PptxResponseSchema>;
export type AmpFileMessage = z.infer<typeof AmpFileResponseSchema>;
export type ReasoningMessage = z.infer<typeof ReasoningMessage.schema>;
export type WebSearchMessage = z.infer<typeof WebSearchMessage.schema>;
export type CodeInterpreterMessage = z.infer<
  typeof CodeInterpreterMessage.schema
>;
export type ErrorMessage = z.infer<typeof ErrorMessage.schema>;
export type CanceledMessage = z.infer<typeof CanceledMessage.schema>;
export type SuggestionsMessage = z.infer<typeof SuggestionsMessage.schema>;
export type EmailMessage = z.infer<typeof EmailMessage.schema>;
export type AssumptionsMessage = z.infer<
  typeof AssumptionsResponseSchemaWithDefinitionIds
>;
export type AssumptionsAgentMessage = z.infer<
  typeof AssumptionsAgentResponseSchema
>;
export type QuizMessage = z.infer<typeof QuizResponseSchema>;
export type GettySelectionMessage = z.infer<
  typeof GettySelectionResponseSchema
>;
export type ModeTransitionMessage = z.infer<
  typeof ModeTransitionResponseSchema
>;
export type SaveMemoryMessage = z.infer<typeof SaveMemoryResponseSchema>;
export type PersonalizationMessage = z.infer<
  typeof PersonalizationResponseSchema
>;
export type CampaignArtifactMessage = z.infer<
  typeof CampaignArtifactResponseSchema
>;
export type CampaignConceptMessage = z.infer<
  typeof CampaignConceptResponseSchema
>;
export type ConceptBriefMessage = z.infer<typeof ConceptBriefResponseSchema>;
export type ArtifactImageMessage = z.infer<typeof ArtifactImageResponseSchema>;
export type ReportPromptMessage = z.infer<typeof ReportPromptResponseSchema>;
export type EmailBestPracticeAnalysisMessage = z.infer<
  typeof EmailBestPracticeAnalysisResponseSchema
>;
export type EmailComponentCanonicalizationMessage = z.infer<
  typeof EmailComponentCanonicalizationResponseSchema
>;
export type ContextReviewMessage = z.infer<typeof ContextReviewResponseSchema>;
export type ContextSuggestionsMessage = z.infer<
  typeof ContextSuggestionsResponseSchema
>;
export type CopyExamplesReviewMessage = z.infer<
  typeof CopyExamplesReviewResponseSchema
>;
export type PersistedCopyExamplesReviewMessage = z.infer<
  typeof PersistedCopyExamplesReviewMessageSchema
>;
export type SuggestionBatchMessage = z.infer<
  typeof SuggestionBatchResponseSchema
>;
export type ReportSuggestionsMessage = z.infer<
  typeof ReportSuggestionsMessageSchema
>;
export type CampaignInsightsResultMessage = z.infer<
  typeof CampaignInsightsResultMessageSchema
>;
export type { IdrChangePlanMessage } from "../skills/idr/llm-output";
export type ContextReviewSuggestion =
  ContextReviewMessage["content"]["suggestions"][number];
export type ContextReviewOperation = ContextReviewSuggestion["operation"];

// These are internal steps that an assistant can return. We don't ask for them in the response schema, but we want to parse them.
export const InternalStepMessageSchema = z.discriminatedUnion("type", [
  ToolMessage.schema,
  ReasoningMessage.schema,
  WebSearchMessage.schema,
  CodeInterpreterMessage.schema,
]);

const internalStepTypes = [
  ToolMessage.type,
  ReasoningMessage.type,
  WebSearchMessage.type,
  CodeInterpreterMessage.type,
  DeveloperMessage.type,
];

export const isInternalStepMessage = (
  message: ResponseMessage,
): message is z.infer<typeof InternalStepMessageSchema> => {
  return internalStepTypes.includes(message.type);
};

// All possible response messages should be defined here.
export const ResponseMessageSchema = z.discriminatedUnion("type", [
  CalendarDefinitionsResponseSchema,
  UserMessage.schema,
  BaseAssistantMessage.schema,
  TurnCompleteMessage.schema,
  AudienceResponseSchema,
  JourneyResponseSchema,
  JourneyReferenceResponseSchema,
  NdEmailMessageResponseSchema,
  TraitResponseSchema,
  AdBriefResponseSchema,
  AdBriefAssetResponseSchema,
  CsvResponseSchema,
  PptxResponseSchema,
  ErrorMessage.schema,
  CanceledMessage.schema,
  SuggestionsMessage.schema,
  InternalStepMessageSchema,
  EmailMessage.schema,
  ContentEmailMessage.schema,
  AmpFileResponseSchema,
  AssumptionsResponseSchemaWithDefinitionIds,
  QuizResponseSchema,
  GettySelectionResponseSchema,
  ModeTransitionResponseSchema,
  SaveMemoryResponseSchema,
  PersonalizationResponseSchema,
  CampaignArtifactResponseSchema,
  CampaignConceptResponseSchema,
  ConceptBriefResponseSchema,
  DeveloperMessage.schema,
  ArtifactReviewResponseSchema,
  ArtifactImageResponseSchema,
  ReportPromptResponseSchema,
  EmailBestPracticeAnalysisResponseSchema,
  ContextReviewResponseSchema,
  ContextSuggestionsResponseSchema,
  CopyExamplesReviewResponseSchema,
  EmailTemplateAnalysisResponseSchema,
  EmailComponentCanonicalizationResponseSchema,
  SuggestionBatchResponseSchema,
  ReportSuggestionsMessageSchema,
  IdrChangePlanResponseSchema,
  CampaignInsightsResultMessageSchema,
]);

type ResponseMessageMetadata = {
  messageId?: string;
  messageContentIndex?: number;
  createdAt?: string;
  completedAt?: string;
};

export type PersistedResponseMessage = z.infer<typeof ResponseMessageSchema> &
  ResponseMessageMetadata;

type ParsedResponseMessage = z.infer<typeof ResponseMessageSchema>;
// Keep existing consumers on the legacy journey variant until every
// persisted-message read surface dispatches on envelope versus legacy shape.
export type ResponseMessage = (
  | Exclude<ParsedResponseMessage, { type: "journey" }>
  | JourneyMessage
) &
  ResponseMessageMetadata;
